import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Key, Plus, ArrowLeft, Play, Clock, CheckCircle } from 'lucide-react';

export default function App() {
  // Navigation State: 'home' | 'student-login' | 'student-quiz' | 'admin-login' | 'admin-dashboard' | 'result'
  const [view, setView] = useState('home');
  
  // App Data State
  const [adminPIN, setAdminPIN] = useState('123456'); // PIN Default Admin
  const [inputPIN, setInputPIN] = useState('');
  const [quizzes, setQuizzes] = useState([
    {
      code: '123456',
      title: 'KUIS',
      timer: 30,
      questions: [
        {
          question: 'Proses pengolahan susu menggunakan suhu tinggi singkat disebut?',
          options: ['Pasteurisasi', 'Sterilisasi', 'Fermentasi', 'Evaporasi'],
          correct: 0
        }
      ]
    }
  ]);

  // Student State
  const [studentName, setStudentName] = useState('');
  const [quizCode, setQuizCode] = useState('');
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  // Admin New Quiz Form State
  const [newTitle, setNewTitle] = useState('');
  const [newTimer, setNewTimer] = useState(30);
  const [newQuestions, setNewQuestions] = useState([
    { question: '', options: ['', '', '', ''], correct: 0 }
  ]);

  // Timer Effect for Student Quiz
  useEffect(() => {
    if (view === 'student-quiz' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (view === 'student-quiz' && timeLeft === 0) {
      handleNextQuestion();
    }
  }, [timeLeft, view]);

  // Handlers
  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (inputPIN === adminPIN) {
      setView('admin-dashboard');
      setInputPIN('');
    } else {
      alert('Kode Akses Admin Salah!');
    }
  };

  const handleStudentJoin = (e) => {
    e.preventDefault();
    const foundQuiz = quizzes.find(q => q.code === quizCode);
    if (!foundQuiz) {
      alert('Kode kuis 6 digit tidak ditemukan!');
      return;
    }
    if (!studentName.trim()) {
      alert('Harap masukkan nama/tim Anda!');
      return;
    }
    setActiveQuiz(foundQuiz);
    setCurrentQuestionIndex(0);
    setScore(0);
    setTimeLeft(foundQuiz.timer);
    setView('student-quiz');
  };

  const handleSelectOption = (index) => {
    setSelectedOption(index);
  };

  const handleNextQuestion = () => {
    if (selectedOption === activeQuiz.questions[currentQuestionIndex].correct) {
      setScore(score + 1);
    }
    setSelectedOption(null);

    if (currentQuestionIndex + 1 < activeQuiz.questions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setTimeLeft(activeQuiz.timer);
    } else {
      setView('result');
    }
  };

  const handleCreateQuiz = (e) => {
    e.preventDefault();
    const generatedCode = Math.floor(100000 + Math.random() * 900000).toString();
    const createdQuiz = {
      code: generatedCode,
      title: newTitle,
      timer: parseInt(newTimer),
      questions: newQuestions
    };
    setQuizzes([...quizzes, createdQuiz]);
    alert(`Kuis Berhasil Dibuat! Kode Kuis Mahasiswa: ${generatedCode}`);
    setNewTitle('');
    setNewQuestions([{ question: '', options: ['', '', '', ''], correct: 0 }]);
  };

  const handleAddQuestionField = () => {
    setNewQuestions([...newQuestions, { question: '', options: ['', '', '', ''], correct: 0 }]);
  };

  const resetToHome = () => {
    setStudentName('');
    setQuizCode('');
    setActiveQuiz(null);
    setCurrentQuestionIndex(0);
    setScore(0);
    setView('home');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
      
      {/* 1. HALAMAN UTAMA (HOME) */}
      {view === 'home' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2">FOOD TECH MILLIONAIRE</h1>
          <p className="text-slate-500 mb-8">Sesi Ice Breaking Interaktif S1 Teknologi Pangan</p>
          <div className="space-y-4">
            <button
              onClick={() => setView('admin-login')}
              className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition shadow-md"
            >
              <Monitor size={20} /> Masuk Layar Dosen
            </button>
            <button
              onClick={() => setView('student-login')}
              className="w-full flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition shadow-md"
            >
              <Smartphone size={20} /> Masuk Sebagai Mahasiswa
            </button>
          </div>
        </div>
      )}

      {/* 2. LOGIN MAHASISWA */}
      {view === 'student-login' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <button onClick={() => setView('home')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-6">
            <ArrowLeft size={18} /> Kembali
          </button>
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Gabung Kuis Mahasiswa</h2>
          <form onSubmit={handleStudentJoin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nama Mahasiswa / Tim</label>
              <input
                type="text"
                required
                placeholder="Contoh: Kelompok 1"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kode Kuis (6 Digit)</label>
              <input
                type="text"
                maxLength={6}
                required
                placeholder="Masukkan 6 angka PIN"
                value={quizCode}
                onChange={(e) => setQuizCode(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none font-mono text-center text-lg tracking-widest"
              />
            </div>
            <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition">
              Mulai Mengerjakan
            </button>
          </form>
        </div>
      )}

      {/* 3. LOGIN DOSEN */}
      {view === 'admin-login' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <button onClick={() => setView('home')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-6">
            <ArrowLeft size={18} /> Kembali
          </button>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Akses Dosen / Admin</h2>
          <p className="text-slate-500 text-sm mb-6">Default PIN: 123456</p>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kode Akses Admin</label>
              <input
                type="password"
                required
                placeholder="Masukkan PIN Admin"
                value={inputPIN}
                onChange={(e) => setInputPIN(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-center text-lg"
              />
            </div>
            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition">
              Masuk Dashboard
            </button>
          </form>
        </div>
      )}

      {/* DASHBOARD DOSEN (PENGATURAN KUIS) */}
      {view === 'admin-dashboard' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl w-full">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Dashboard Dosen - Buat Kuis Baru</h2>
            <button onClick={resetToHome} className="text-slate-500 hover:text-slate-800 text-sm font-medium">
              Keluar
            </button>
          </div>

          <form onSubmit={handleCreateQuiz} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Judul Kuis</label>
              <input
                type="text"
                required
                placeholder="Misal: Kuis Uji Sensori Pangan"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Waktu Per Soal (Detik)</label>
              <input
                type="number"
                required
                value={newTimer}
                onChange={(e) => setNewTimer(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-800 mb-4">Daftar Pertanyaan</h3>
              {newQuestions.map((q, qIndex) => (
                <div key={qIndex} className="p-4 bg-slate-50 rounded-xl mb-4 border space-y-3">
                  <input
                    type="text"
                    required
                    placeholder={`Pertanyaan ${qIndex + 1}`}
                    value={q.question}
                    onChange={(e) => {
                      const updated = [...newQuestions];
                      updated[qIndex].question = e.target.value;
                      setNewQuestions(updated);
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((opt, optIndex) => (
                      <input
                        key={optIndex}
                        type="text"
                        required
                        placeholder={`Pilihan ${optIndex + 1}`}
                        value={opt}
                        onChange={(e) => {
                          const updated = [...newQuestions];
                          updated[qIndex].options[optIndex] = e.target.value;
                          setNewQuestions(updated);
                        }}
                        className="w-full px-3 py-1.5 border rounded-md text-sm"
                      />
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mr-2">Jawaban Benar:</label>
                    <select
                      value={q.correct}
                      onChange={(e) => {
                        const updated = [...newQuestions];
                        updated[qIndex].correct = parseInt(e.target.value);
                        setNewQuestions(updated);
                      }}
                      className="text-sm p-1 border rounded"
                    >
                      <option value={0}>Pilihan 1</option>
                      <option value={1}>Pilihan 2</option>
                      <option value={2}>Pilihan 3</option>
                      <option value={3}>Pilihan 4</option>
                    </select>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddQuestionField}
                className="flex items-center gap-2 text-indigo-600 font-semibold text-sm hover:underline mt-2"
              >
                <Plus size={16} /> Tambah Pertanyaan Lain
              </button>
            </div>

            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition shadow-md">
              Terbitkan Kuis & Dapatkan Kode Akses
            </button>
          </form>
        </div>
      )}

      {/* HALAMAN PENGERJAAN KUIS MAHASISWA */}
      {view === 'student-quiz' && activeQuiz && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-xl w-full">
          <div className="flex justify-between items-center mb-6">
            <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              {studentName}
            </span>
            <div className="flex items-center gap-1 font-mono font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              <Clock size={16} /> {timeLeft}s
            </div>
          </div>

          <h3 className="text-lg font-bold text-slate-800 mb-4">
            Soal {currentQuestionIndex + 1} dari {activeQuiz.questions.length}
          </h3>
          <p className="text-slate-700 mb-6 font-medium">
            {activeQuiz.questions[currentQuestionIndex].question}
          </p>

          <div className="space-y-3 mb-6">
            {activeQuiz.questions[currentQuestionIndex].options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectOption(idx)}
                className={`w-full text-left p-4 rounded-xl border transition ${
                  selectedOption === idx
                    ? 'border-emerald-600 bg-emerald-50 font-semibold text-emerald-900'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          <button
            onClick={handleNextQuestion}
            disabled={selectedOption === null}
            className="w-full bg-emerald-600 disabled:opacity-50 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition"
          >
            {currentQuestionIndex + 1 === activeQuiz.questions.length ? 'Selesai' : 'Lanjut Soal Berikutnya'}
          </button>
        </div>
      )}

      {/* 4. HALAMAN SELESAI / HASIL (DENGAN TOMBOL KEMBALI KE MENU UTAMA) */}
      {view === 'result' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <CheckCircle className="mx-auto text-emerald-500 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Kuis Selesai!</h2>
          <p className="text-slate-500 mb-6">Terima kasih telah berpartisipasi, <span className="font-semibold text-slate-800">{studentName}</span>.</p>
          
          <div className="bg-slate-50 p-4 rounded-xl mb-6">
            <span className="text-sm text-slate-500 block">Skor Akhir Anda</span>
            <span className="text-4xl font-extrabold text-emerald-600">{score} / {activeQuiz.questions.length}</span>
          </div>

          {/* Tombol Kembali Ke Menu Utama */}
          <button
            onClick={resetToHome}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-3 rounded-xl transition shadow-md"
          >
            Kembali ke Menu Utama
          </button>
        </div>
      )}

    </div>
  );
}